/**
 * Composant ProjectHealthGauge
 *
 * Jauge circulaire SVG affichant le score de santé du projet
 */

import React, { useEffect, useState } from 'react';
import { Activity, Zap } from 'lucide-react';
import { computeHealthScore } from '../../utils/dashboardMetrics';

const RADIUS = 54;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const COLOR_MAP = {
  green: '#10B981',
  orange: '#F59E0B',
  red: '#EF4444'
};

const LABEL_MAP = {
  green: 'Excellent',
  orange: 'Correct',
  red: 'Attention'
};

// ── Carte Sprint Actif ──────────────────────────────────────────
const SprintCard = ({ usStageGroups }) => {
  const byCategory = {};
  usStageGroups.forEach(g => {
    const c = g.category;
    if (!byCategory[c]) byCategory[c] = { count: 0, sp: 0 };
    byCategory[c].count += g.count;
    byCategory[c].sp    += g.totalSP || 0;
  });

  const get = (keys) => keys.reduce((acc, k) => acc + (byCategory[k]?.count || 0), 0);
  const getSP = (keys) => keys.reduce((acc, k) => acc + (byCategory[k]?.sp || 0), 0);

  const done       = get(['Done', 'En Test', 'Test PO', 'Test testeur', 'Tests à valider']);
  const remaining  = get(['Backlog Sprint', 'En cours', 'Daily']);
  const total      = done + remaining;

  const completedSP = getSP(['Done', 'En Test', 'Test PO', 'Test testeur', 'Tests à valider']);
  const totalSP     = completedSP + getSP(['Backlog Sprint', 'En cours', 'Daily']);

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const offset = CIRCUMFERENCE - (pct / 100) * CIRCUMFERENCE;

  const [animOffset, setAnimOffset] = useState(CIRCUMFERENCE);
  useEffect(() => {
    const t = setTimeout(() => setAnimOffset(offset), 100);
    return () => clearTimeout(t);
  }, [offset]);

  const rows = [
    { label: 'Done',       count: done,      sp: completedSP,                                    color: '#10B981' },
    { label: 'Restantes',  count: remaining, sp: getSP(['Backlog Sprint', 'En cours', 'Daily']), color: '#D1D5DB' },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-6 flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
          <Zap className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Sprint Actif</h3>
          <p className="text-xs text-gray-500">Avancement du sprint en cours</p>
        </div>
      </div>

      <div className="flex flex-col items-center flex-1 justify-center">
        <div className="relative">
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle cx="70" cy="70" r={RADIUS} fill="none" stroke="#F3F4F6" strokeWidth="12" />
            <circle
              cx="70" cy="70" r={RADIUS}
              fill="none"
              stroke="#7C3AED"
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={animOffset}
              style={{ transition: 'stroke-dashoffset 1s ease-in-out', transform: 'rotate(-90deg)', transformOrigin: '70px 70px' }}
            />
            <text x="70" y="66" textAnchor="middle" fontSize="28" fontWeight="bold" fill="#111827">{pct}</text>
            <text x="70" y="84" textAnchor="middle" fontSize="12" fill="#9CA3AF">%</text>
          </svg>
        </div>

        <span className="mt-2 px-3 py-1 rounded-full text-xs font-bold text-white bg-purple-600">
          {done}/{total} US · {completedSP}/{totalSP} SP
        </span>

        <div className="mt-4 w-full space-y-2">
          {rows.map(r => (
            <div key={r.label} className="flex justify-between text-xs text-gray-600">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: r.color }} />
                <span>{r.label}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400">{r.count} US</span>
                <span className="font-semibold">{r.sp} SP</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Carte Score Santé ────────────────────────────────────────────
const ProjectHealthGauge = ({ usStageGroups = [], milestones = [], tickets = [], sprintActif = false, doneTasksCount = null, totalTasksCount = null, resolvedTicketsCount = null, botResolvedTicketsCount = 0, totalTicketsCount = null }) => {
  const {
    score, storyScore, milestoneScore, ticketScore, color,
    doneStories, totalStories,
    completedMilestones, totalMilestones,
    resolvedTickets, totalTickets
  } = computeHealthScore(usStageGroups, milestones, tickets, doneTasksCount, totalTasksCount, resolvedTicketsCount, totalTicketsCount);

  const [animatedOffset, setAnimatedOffset] = useState(CIRCUMFERENCE);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnimatedOffset(CIRCUMFERENCE - (score / 100) * CIRCUMFERENCE);
    }, 100);
    return () => clearTimeout(timeout);
  }, [score]);

  if (sprintActif) return <SprintCard usStageGroups={usStageGroups} />;

  const strokeColor = COLOR_MAP[color];
  const label = LABEL_MAP[color];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
          <Activity className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Score Santé</h3>
          <p className="text-xs text-gray-500">Santé globale du projet</p>
        </div>
      </div>

      {/* Gauge SVG */}
      <div className="flex flex-col items-center flex-1 justify-center">
        <div className="relative">
          <svg width="140" height="140" viewBox="0 0 140 140">
            {/* Track */}
            <circle
              cx="70"
              cy="70"
              r={RADIUS}
              fill="none"
              stroke="#F3F4F6"
              strokeWidth="12"
            />
            {/* Progress */}
            <circle
              cx="70"
              cy="70"
              r={RADIUS}
              fill="none"
              stroke={strokeColor}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={animatedOffset}
              style={{
                transition: 'stroke-dashoffset 1s ease-in-out',
                transform: 'rotate(-90deg)',
                transformOrigin: '70px 70px'
              }}
            />
            {/* Score text */}
            <text
              x="70"
              y="66"
              textAnchor="middle"
              fontSize="28"
              fontWeight="bold"
              fill="#111827"
            >
              {score}
            </text>
            <text
              x="70"
              y="84"
              textAnchor="middle"
              fontSize="12"
              fill="#9CA3AF"
            >
              %
            </text>
          </svg>
        </div>

        {/* Status badge */}
        <span
          className="mt-2 px-3 py-1 rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: strokeColor }}
        >
          {label}
        </span>

        {/* Details */}
        <div className="mt-4 w-full space-y-2">
          <div className="flex justify-between text-xs text-gray-600">
            <span>US complétées</span>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400">{doneStories}/{totalStories}</span>
              <span className="font-semibold">{storyScore}%</span>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>Jalons atteints</span>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400">{completedMilestones}/{totalMilestones}</span>
              <span className="font-semibold">{milestoneScore}%</span>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>Tickets résolus par le support</span>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400">{resolvedTickets}/{totalTickets}</span>
              <span className="font-semibold">{ticketScore}%</span>
            </div>
          </div>
          <div className="flex justify-between text-xs text-purple-700">
            <span>Résolus par Dynamix</span>
            <span className="font-semibold">{botResolvedTicketsCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectHealthGauge;
